import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ServiceTitanInvoiceLink } from "@/core/components/service-titan-invoice-link";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatMoney } from "@/core/utils/money";
import type { IcmJob } from "@/icm/types";
import {
  getPayBreakdownStatementPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";

interface IcmJobSidebarProps {
  job: IcmJob;
  periodStart?: string;
  onClose: () => void;
}

export const IcmJobSidebar = ({
  job,
  periodStart,
  onClose,
}: IcmJobSidebarProps) => {
  const navigate = useNavigate();

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      <SimpleSidebarHeader
        title={job.customer_name ?? "–"}
        actions={
          job.job_recall_for_id && (
            <Badge variant="secondary" color="orange" size="sm">
              Callback
            </Badge>
          )
        }
        onClose={onClose}
      />
      <Stack align="start" gap={6} className="w-full px-5 py-4">
        {job.job_id ? (
          <ServiceTitanJobLink jobId={job.job_id} />
        ) : (
          job.invoice_id && (
            <ServiceTitanInvoiceLink invoiceId={job.invoice_id} />
          )
        )}

        <Stack className="w-full" align="start" gap={4}>
          <Stack className="w-full" align="start" direction="row" gap={8}>
            {job.job_completed_on && (
              <Stack align="start" gap={1} className="w-[50%]">
                <Text variant="label" tone="muted">
                  Completed
                </Text>
                <Text>
                  {dayjs(job.job_completed_on).format("MMM DD, YYYY")}
                </Text>
              </Stack>
            )}
            <Stack align="start" gap={1} className="w-[50%]">
              <Text variant="label" tone="muted">
                Job total
              </Text>
              <Text>
                {job.job_total != null
                  ? formatMoney(job.job_total.toFixed(2))
                  : "-"}
              </Text>
            </Stack>
          </Stack>
          {job.business_unit_name && (
            <Stack align="start" gap={1}>
              <Text variant="label" tone="muted">
                Business unit
              </Text>
              <Text>{job.business_unit_name}</Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      <div className="w-full flex-1 overflow-y-auto">
        <Stack align="start" gap={1} className="w-full px-5 py-2">
          {job.payouts.map((payout, i) => (
            <Stack
              className="w-full py-2"
              align="center"
              key={i}
              gap={4}
              direction="row"
              justify="between"
            >
              <Stack align="start" gap={1}>
                <Button
                  variant="link"
                  size="xs"
                  className="h-auto p-0 font-normal"
                  onClick={() =>
                    navigate(
                      periodStart
                        ? getPayBreakdownStatementPath(
                            periodStart,
                            payout.statement_id,
                          )
                        : `${PAY_BREAKDOWN_PATH}/statements/${payout.statement_id}`,
                    )
                  }
                >
                  {payout.technician_name ?? "–"}
                </Button>
                <Text tone="muted">{payout.payout_rule_name}</Text>
              </Stack>
              <Text>{formatMoney(payout.payout_amount)}</Text>
            </Stack>
          ))}
          <Stack
            gap={4}
            direction="row"
            justify="between"
            align="center"
            className="w-full py-3"
          >
            <Text variant="label" tone="muted">
              Total pay
            </Text>
            <Text variant="title">{formatMoney(job.total_payout)}</Text>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};
